import { useEffect, useState } from "react";
import { Alert, Button, Card, FileButton, Group, Image, Stack, Text, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deleteOwnSignature, getOwnSignature, putOwnSignature } from "@/api/branding";
import {
  IMAGE_MAX_EDGE_PX,
  SIGNATURE_MAX_BYTES,
  checkImage,
  fileBytes,
  formatBytes,
  type ImageRefusal,
} from "@/branding/images";
import { AppLayout } from "@/components/AppLayout";
import { t, type MessageKey } from "@/i18n";

/**
 * My signature (Gate B white-labelling, plan B1, D5): a lab doctor's own signature image,
 * printed with their name on the reports they approve.
 *
 * **Only the caller's own.** The route needs `signature:manage-own`, and the server takes
 * the doctor from the sign-in, so there is nothing on this page that could name someone
 * else. A new signature applies to reports approved from then on: an approval captures the
 * signature in force at that moment (contract 4.6), so a report already approved keeps the
 * one it was approved with.
 */

const REFUSAL_TEXT: Record<ImageRefusal, MessageKey> = {
  image_type_refused: "branding.image_type_refused",
  image_too_large: "branding.image_too_large",
  image_dimensions: "branding.image_dimensions",
};

export function MySignaturePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const current = useQuery({
    queryKey: ["my-signature"],
    queryFn: getOwnSignature,
    retry: false,
  });

  useEffect(
    () => () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const upload = useMutation({
    mutationFn: putOwnSignature,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-signature"] });
    },
  });
  const remove = useMutation({
    mutationFn: deleteOwnSignature,
    onSuccess: async () => {
      setPreview(null);
      await queryClient.invalidateQueries({ queryKey: ["my-signature"] });
    },
  });

  async function picked(file: File | null): Promise<void> {
    setProblem(null);
    if (file === null) return;
    const check = checkImage(await fileBytes(file), "signature");
    if (!check.ok) {
      setProblem(t(REFUSAL_TEXT[check.refusal]));
      return;
    }
    upload.mutate(file, {
      onSuccess: () => {
        if (typeof URL.createObjectURL === "function") setPreview(URL.createObjectURL(file));
      },
    });
  }

  const failure = upload.error ?? remove.error;
  return (
    <AppLayout breadcrumbs={[{ label: t("signature.title") }]}>
      <Title order={1}>{t("signature.title")}</Title>
      <Text c="dimmed">{t("signature.subtitle")}</Text>
      <Card withBorder data-testid="my-signature">
        {current.isPending ? (
          <Text c="dimmed">{t("signature.loading")}</Text>
        ) : current.isError ? (
          <Alert color="red" title={t("signature.unavailable")}>
            {current.error instanceof Error ? current.error.message : null}
          </Alert>
        ) : (
          <Stack gap="sm">
            {current.data.sha256 !== null ? (
              <Group gap="sm" data-testid="my-signature-on-file">
                {preview !== null && (
                  <Image src={preview} alt={t("signature.title")} h={60} w="auto" fit="contain" />
                )}
                <Text size="sm">
                  {t("signature.on_file")} {current.data.version}
                </Text>
              </Group>
            ) : (
              <Text size="sm" c="dimmed" data-testid="my-signature-none">
                {t("signature.none")}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {t("branding.image_limits")
                .replace("{cap}", formatBytes(SIGNATURE_MAX_BYTES))
                .replace("{edge}", String(IMAGE_MAX_EDGE_PX))}
            </Text>
            <Group>
              <FileButton onChange={(f) => void picked(f)} accept="image/png,image/jpeg">
                {(props) => (
                  <Button {...props} loading={upload.isPending} data-testid="my-signature-pick">
                    {current.data.sha256 !== null ? t("signature.replace") : t("signature.upload")}
                  </Button>
                )}
              </FileButton>
              {current.data.sha256 !== null && (
                <Button
                  variant="subtle"
                  color="gray"
                  loading={remove.isPending}
                  onClick={() => remove.mutate()}
                  data-testid="my-signature-remove"
                >
                  {t("signature.remove")}
                </Button>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {t("signature.applies_hint")}
            </Text>
            {(problem !== null || failure !== null) && (
              <Alert color="red" p="xs" data-testid="my-signature-error">
                {problem ?? (failure instanceof Error ? failure.message : null)}
              </Alert>
            )}
          </Stack>
        )}
      </Card>
    </AppLayout>
  );
}
